import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import ModuleLayout from '../common/ModuleLayout';

const { FiTarget } = FiIcons;

const ModuleOne = ({ data, onSave, completed }) => {
  const [formData, setFormData] = useState({
    callingPrompt: '',
    burdenNudge: '',
    readerName: '',
    readerStruggle: '',
    readerLonging: '',
    spiritualTransformation: '',
    emotionalTransformation: '',
    practicalTransformation: '',
    biblicalAnchor: ''
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFormData(prev => ({ ...prev, ...data }));
  }, [data]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateClarityStatement = () => {
    const { readerName, readerStruggle, spiritualTransformation, biblicalAnchor } = formData;
    if (readerName && readerStruggle && spiritualTransformation && biblicalAnchor) {
      return `This book exists to help ${readerName} move from ${readerStruggle} to ${spiritualTransformation} through ${biblicalAnchor}.`;
    }
    return '';
  };

  const handleSave = async (markComplete) => {
    setIsLoading(true);
    const clarityStatement = generateClarityStatement();
    const dataToSave = { ...formData, clarityStatement };
    
    setTimeout(() => {
      onSave(dataToSave, markComplete);
      setIsLoading(false);
    }, 500);
  };

  const canComplete = Object.values(formData).every(value => value.trim() !== '');
  const clarityStatement = generateClarityStatement();

  return (
    <ModuleLayout
      title="Clarify the Calling"
      description="Discern why this book exists, who it's for, and the transformation God intends it to bring."
      icon={FiTarget}
      onSave={handleSave}
      completed={completed}
      canComplete={canComplete}
      isLoading={isLoading}
    >
      <div className="space-y-8">
        {/* Calling Prompt */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <label className="block text-lg font-semibold text-gray-900 mb-3">
            What is God calling you to write? Why does this book matter now?
          </label>
          <textarea
            value={formData.callingPrompt}
            onChange={(e) => handleInputChange('callingPrompt', e.target.value)}
            rows={6}
            className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder="Share your heart about this calling..."
          />
        </div>

        {/* Burden or Nudge */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <label className="block text-lg font-semibold text-gray-900 mb-3">
            What message, question, or tension won't leave you alone?
          </label>
          <textarea
            value={formData.burdenNudge}
            onChange={(e) => handleInputChange('burdenNudge', e.target.value)}
            rows={4}
            className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder="What keeps stirring in your heart?"
          />
        </div>

        {/* Reader Snapshot */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Reader Snapshot</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reader name or description
              </label>
              <input
                type="text"
                value={formData.readerName}
                onChange={(e) => handleInputChange('readerName', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Struggling new mom, Overwhelmed pastor..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Their current struggle
              </label>
              <input
                type="text"
                value={formData.readerStruggle}
                onChange={(e) => handleInputChange('readerStruggle', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="What pain or challenge are they facing?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Their deepest longing
              </label>
              <input
                type="text"
                value={formData.readerLonging}
                onChange={(e) => handleInputChange('readerLonging', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="What do they truly desire?"
              />
            </div>
          </div>
        </div>

        {/* Transformation Outcomes */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Transformation Outcomes</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Spiritual transformation
              </label>
              <textarea
                value={formData.spiritualTransformation}
                onChange={(e) => handleInputChange('spiritualTransformation', e.target.value)}
                rows={3}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="How will their relationship with God change?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Emotional transformation
              </label>
              <textarea
                value={formData.emotionalTransformation}
                onChange={(e) => handleInputChange('emotionalTransformation', e.target.value)}
                rows={3}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="How will they feel differently?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Practical transformation
              </label>
              <textarea
                value={formData.practicalTransformation}
                onChange={(e) => handleInputChange('practicalTransformation', e.target.value)}
                rows={3}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="What will they do differently?"
              />
            </div>
          </div>
        </div>

        {/* Biblical Anchor */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <label className="block text-lg font-semibold text-gray-900 mb-3">
            Scripture, theme, or gospel truth grounding this book
          </label>
          <textarea
            value={formData.biblicalAnchor}
            onChange={(e) => handleInputChange('biblicalAnchor', e.target.value)}
            rows={4}
            className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder="What biblical foundation anchors your message?"
          />
        </div>

        {/* Auto-Generated Clarity Statement */}
        {clarityStatement && (
          <div className="bg-gradient-to-r from-blue-50 to-orange-50 rounded-xl p-6 border border-blue-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Clarity Statement</h3>
            <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500">
              <p className="text-gray-800 font-medium italic">
                "{clarityStatement}"
              </p>
            </div>
          </div>
        )}
      </div>
    </ModuleLayout>
  );
};

export default ModuleOne;